import { queryKeys } from '@core/Data/ReactQuery/constants'
import { useQuery } from '@tanstack/react-query'
import { getBetLoopCanShareTickets } from '@core/Areas/BetLoop/helpers/api'
import type { Ticket } from '@core/Areas/AccountActivity/types'
import { isFauxTicketTransaction } from '@core/Areas/AccountActivity/helpers'
import { fetchPendingTicketsData } from '@core/Areas/AccountActivity/helpers/api'
import { shouldAllowShareInBetLoop } from '@core/Areas/BetLoop/helpers/shouldAllowShareInBetLoop'
import { canAllowShareForFuturesTicket } from '@core/Areas/AccountActivity/Components/Ticket/Components/ShareBet/helpers/canAllowShareForFuturesTicket'

export const useSharePendingTicketsData = () => {
  const { isLoading, isFetching, data, isError } = useQuery({
    queryKey: [queryKeys.betLoopSharePendingTickets, 'racing'],
    queryFn: () => fetchTicketsData(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const racingTickets = data?.data || []
  const isEmptyState = !isLoading && !isFetching && racingTickets.length === 0

  return {
    racingTickets,
    isError,
    isLoading: isLoading || isFetching,
    isEmptyState,
  }
}

// =============
// Local Helpers
// =============

const fetchTicketsData = async (): Promise<FetchPendingTicketsData> => {
  const pendingTicketsData = await fetchPendingTicketsData({
    activeTab: 'pending',
    isOrderByEventStart: true,
    nextPage: null,
  })

  const tickets = Object.values(pendingTicketsData.ticketsById)
  const ticketSequences = Object.keys(pendingTicketsData.ticketsById)

  if (ticketSequences.length === 0) return { data: [] }

  const canShareData = await getBetLoopCanShareTickets(ticketSequences, [])

  const shareableSequences = canShareData.flatMap(ticket =>
    !ticket.canShare || !ticket.ticketSequence ? [] : [ticket.ticketSequence]
  )

  if (shareableSequences.length === 0) return { data: [] }

  const filteredTickets = tickets.flatMap(ticket => {
    if (isFauxTicketTransaction(ticket) || !canAllowShareForFuturesTicket('pending', ticket)) return []

    const shouldAllowShare =
      shareableSequences.includes(ticket.ticketSequence) &&
      shouldAllowShareInBetLoop({
        contextType: 'ticket',
        share: { ticket, activeTab: 'pending', ticketSequence: ticket.ticketSequence },
      })
    return shouldAllowShare ? [ticket] : []
  })

  return { data: filteredTickets }
}

// =====
// Types
// =====

type FetchPendingTicketsData = {
  data: Ticket[]
}
